import React, { CSSProperties } from 'react'
import { Color as ColorType } from 'modules/shares/types'
import styled, { css } from 'styled-components'
import ColorBackground from 'icons/color-background.svg'
import rgbTo, { ColorFormat } from './utils'
import Opacity from '../Opacity'
import { truncateStyles } from 'components/Truncate'

export const DEFAULT_MAX_TEXT_WIDTH = 128

const Container = styled.div`
  align-items: center;
  display: flex;
  font-size: 0.8125rem;
  letter-spacing: -0.07px;
`

export interface ColorProps extends ColorType {
  style?: CSSProperties
  displayOpacity?: boolean
  connected?: boolean
  format?: ColorFormat
  maxTextWidth?: number
  className?: string
}

const ColorIconUnstyled = ({
  red,
  green,
  blue,
  alpha,
  style,
  connected,
  ...props
}: ColorProps) => (
  <div
    role="img"
    aria-label={`rgba(${red}, ${green}, ${blue}, ${alpha})`}
    {...props}
    style={{
      ...style,
      background: `rgba(${red}, ${green}, ${blue}, ${alpha})`,
    }}
  />
)

export const ColorIcon = styled(ColorIconUnstyled)<{
  iconSize?: number
}>`
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border.dark.extraHigh};
  display: inline-block;
  margin-right: 8px;
  position: relative;
  height: ${props => props.iconSize ?? 20}px;
  width: ${props => props.iconSize ?? 20}px;
  flex: none;

  &::before {
    content: '';
    background-image: url(${ColorBackground});
    background-position: center center;
    background-size: cover;
    border-radius: 50%;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
  }

  ${({ theme, connected }) => {
    if (connected) {
      return `
        &:after {
          content: '';
          display: block;
          position: absolute;

          top: 16px;
          left: 0;
          right: 0;
          margin: auto;

          width: 1px;
          height: calc(100% - 8px);
          background-color: ${theme.colors.border.dark.extraHigh};
        }
      `
    }
  }}
`

export const textOverflowFadeEffect = (
  maxTextWidth: number = DEFAULT_MAX_TEXT_WIDTH
) => css`
  text-overflow: clip;
  white-space: nowrap;
  overflow: hidden;
  position: relative;
  /* ^ needed to ensure proper positioning of the :after section below */

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    pointer-events: none;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) ${maxTextWidth}px,
      rgba(255, 255, 255, 1) 100%
    );
    /* ^ creates a text-overflow fade effect */
    width: 100%;
    height: 100%;
  }
`

const Text = styled.span<{ maxTextWidth?: number; minTextWidth: number }>`
  color: ${({ theme }) => theme.colors.text.dark.primary};
  max-width: ${props => (props.maxTextWidth || DEFAULT_MAX_TEXT_WIDTH) + 40}px;
  min-width: ${props => props.minTextWidth}px;
  ${truncateStyles};
`
// Safari hack to maintain a min-width (not truncate the colours too early)
function getMinTextWidth(format: ColorFormat) {
  switch (format) {
    case ColorFormat.RGB:
      return 120
    case ColorFormat.HSL:
      return 110
    default:
      return 62
  }
}

const Color: React.FC<ColorProps> = ({
  red,
  green,
  blue,
  alpha = 1,
  displayOpacity = true,
  connected = false,
  format = ColorFormat.HEX,
  maxTextWidth = DEFAULT_MAX_TEXT_WIDTH,
  className,
}) => {
  const colorString = rgbTo(format, { red, green, blue, alpha })
  const minTextWidth = getMinTextWidth(format)

  return (
    <Container className={className}>
      <ColorIcon
        red={red}
        green={green}
        blue={blue}
        alpha={alpha}
        connected={connected}
      />
      <Text
        maxTextWidth={maxTextWidth}
        minTextWidth={minTextWidth}
        className="ColorText">
        {colorString}
      </Text>
      {displayOpacity && format === ColorFormat.HEX && (
        <Opacity value={alpha} />
      )}
    </Container>
  )
}

export default Color
